import React, { memo, useEffect, useRef } from 'react';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { showRemountedAd } from '../biffSearchPage.ts';
import {
  StyledAdv,
  BottomLeaderboardContainer,
  IntraListAdvContainer,
  BottomDoubleMPUContainer,
  BottomDoubleMPULeftContainer,
  BottomDoubleMPURightContainer,
  TopLeaderboardContainer,
} from './styled.ts';

/**
 * Hands the container to biff on every mount, not only the first: a mobile list/map switch remounts the
 * results list, and the ad already served has to come back into the new node (see showRemountedAd). A hidden
 * container is handed over only once it shows: an ad nobody can see is not worth a request.
 */
const useRemountedAd = (id: string, hide: boolean) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hide && ref.current) showRemountedAd({ id, node: ref.current });
  }, [id, hide]);

  return ref;
};

const AdSlot = ({ id, hide }: { id: string; hide: boolean }) => <StyledAdv id={id} ref={useRemountedAd(id, hide)} />;

const IntraListAdSlot = ({ id, hide }: { id: string; hide: boolean }) => (
  <IntraListAdvContainer id={id} hide={hide} ref={useRemountedAd(id, hide)} />
);

type AdvProps = {
  id: string;
  id2?: string;
  hide?: boolean;
  isBottom?: boolean;
  isIntraList?: boolean;
  isDouble?: boolean;
  isTop?: boolean;
};

const Adv: React.FC<AdvProps> = ({ id, id2, hide, isTop, isBottom, isIntraList, isDouble }) => {
  if (isTop) {
    return (
      <ErrorBoundary id="Adv_isTop">
        <TopLeaderboardContainer hide={!!hide}>
          <AdSlot id={id} hide={!!hide} />
        </TopLeaderboardContainer>
      </ErrorBoundary>
    );
  }

  if (isBottom) {
    return (
      <ErrorBoundary id="Adv_isBottom">
        <BottomLeaderboardContainer hide={!!hide}>
          <AdSlot id={id} hide={!!hide} />
        </BottomLeaderboardContainer>
      </ErrorBoundary>
    );
  }

  if (isIntraList) {
    return (
      <ErrorBoundary id="Adv_isIntraList">
        <IntraListAdSlot id={id} hide={!!hide} />
      </ErrorBoundary>
    );
  }

  if (isDouble) {
    return (
      <ErrorBoundary id="Adv_isDouble">
        <BottomDoubleMPUContainer hide={!!hide}>
          <BottomDoubleMPULeftContainer>
            <AdSlot id={id} hide={!!hide} />
          </BottomDoubleMPULeftContainer>
          <BottomDoubleMPURightContainer>{id2 && <AdSlot id={id2} hide={!!hide} />}</BottomDoubleMPURightContainer>
        </BottomDoubleMPUContainer>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary id="Adv">
      <AdSlot id={id} hide={!!hide} />
    </ErrorBoundary>
  );
};

export default memo(Adv);
